import { CompoundEntityRef } from '@backstage/catalog-model';
import type { DefinitionSource } from './definition-source';

/**
 * All supported rule comparison operators, in display order. Single source of
 * truth for both the {@link CheckOperator} type and any UI that lists them.
 */
export const CHECK_OPERATORS = [
  'equals',
  'notEquals',
  'exists',
  'notNull',
  'contains',
  'notContains',
  'matches',
  'startsWith',
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
] as const;

/**
 * Rule comparison operator used by Pulse check conditions.
 */
export type CheckOperator = (typeof CHECK_OPERATORS)[number];

/**
 * Single condition in a check rule, targeting one fact path.
 */
export type CheckRuleCondition = {
  /** "<integrationId>:default/<factId>" */
  factRef: string;
  /** Resolver expression into the fact data, for example "$.spec.owner" or "spec.owner" */
  path: string;
  operator: CheckOperator;
  value?: unknown;
};

/**
 * Recursive check rule composed of `any` or `all` condition groups.
 */
export type CheckRule = {
  any?: (CheckRuleCondition | CheckRule)[];
  all?: (CheckRuleCondition | CheckRule)[];
};

/**
 * Entity kinds that Pulse fact collectors gather facts for. Shared so the
 * collector entity filters and the check-authoring UI stay in sync; checks on
 * other kinds (e.g. User, Group) can never produce results.
 */
export const CHECKABLE_ENTITY_KINDS = [
  'Component',
  'Domain',
  'System',
  'API',
  'Resource',
  'Template',
] as const;

/**
 * Optional entity filter applied during check evaluation.
 */
export type CheckFilter = {
  kind?: string[];
  [key: string]: unknown;
};

/**
 * Fact path resolution strategy.
 */
export type CheckPathResolver = 'jsonpath' | 'jsonata';

export const CHECK_TYPES = ['default', 'manual'] as const;

export type CheckType = (typeof CHECK_TYPES)[number];

/** Maximum number of check definitions accepted by one import request. */
export const CHECK_IMPORT_BATCH_SIZE = 100;

/**
 * Why a check is excluded for an entity in Pulse UI flows.
 */
export const CHECK_EXCLUSION_REASONS = ['user', 'filter'] as const;

/**
 * Reason for excluding a check from evaluation for an entity.
 */
export type CheckExclusionReason = (typeof CHECK_EXCLUSION_REASONS)[number];

/**
 * Full check definition shared between authoring, transport, and execution.
 */
export type CheckDefinition = {
  id: string;
  ownerEntityRef: string;
  name: string;
  description?: string;
  type: CheckType;
  pathResolver: CheckPathResolver;
  rule: CheckRule;
  /**
   * Fact refs treated as sensitive for this specific check.
   */
  sensitiveFacts?: string[];
  filter?: CheckFilter[];
  exclude?: CheckFilter[];
  failedMessage?: string;
  passedMessage?: string;
  /** ISO-8601 timestamp when a DB-managed check was created. */
  createdAt?: string;
  /** ISO-8601 timestamp when a DB-managed check was last updated. */
  updatedAt?: string;
  /**
   * Origin of the definition, populated by the backend on read. `config` checks
   * come from static YAML and are read-only; `db` checks are UI-managed and
   * editable. Not persisted as part of the authored definition.
   */
  source?: DefinitionSource;
};

/** A check that could not be created during a batch YAML import. */
export type CheckImportFailure = {
  id?: string;
  reason: string;
};

/** Per-item outcome returned after processing a check YAML import. */
export type CheckImportResult = {
  total: number;
  imported: string[];
  skipped: string[];
  failed: CheckImportFailure[];
};

/**
 * Per-entity result produced by evaluating a check definition.
 */
export type CheckResult = {
  checkId: string;
  name: string;
  type: CheckDefinition['type'];
  passed: boolean;
  message?: string;
  /** ISO-8601 timestamp of when the check result was last stored. */
  fetchedAt?: string;
};

/**
 * Latest user-provided result and audit metadata for a manual check.
 */
export type ManualCheckResultResponse = {
  result: Omit<CheckResult, 'fetchedAt' | 'type'> & { type: 'manual' };
  justification: string;
  setByUserEntityRef: string;
  /** ISO-8601 timestamp of when the manual result was last set. */
  updatedAt: string;
};

/**
 * User-managed exclusion override for a check on a specific entity.
 */
export type CheckExemptionResponse = {
  checkId: string;
  entityRef: string;
  justification: string;
  setByUserEntityRef: string;
  /** ISO-8601 timestamp of when the exemption was last updated. */
  updatedAt: string;
};

/**
 * Event payload emitted when a fact is collected for an entity.
 */
export type FactCollectedEvent = {
  integrationId: string;
  factId: string;
  entityRef: CompoundEntityRef;
  factData: unknown;
};
